/**
 * CetusEd NextGen — the client session store (vanilla, framework-free).
 *
 * A tiny external store so BOTH React (via useSyncExternalStore in
 * ./SessionProvider) AND the non-React transport (the hc fetch wrapper in
 * ./session-controller) read the same session synchronously.
 *
 * ┌─ SECURITY HONESTY (🪤) — read before changing where tokens live. ─────────┐
 * │ The ACCESS token is kept in MEMORY only (a module variable). It is never   │
 * │ written to localStorage and never placed in React state or the DOM, so a   │
 * │ page reload drops it — that is deliberate, not a bug.                       │
 * │                                                                            │
 * │ The REFRESH token IS persisted so a session can survive a reload — but the  │
 * │ raw storage access is NOT here: it lives in the storage layer              │
 * │ (../storage/session-token-store), which owns the localStorage key and the  │
 * │ XSS-tradeoff note, per the one-gateway rule. Hardening is TASK-00083.      │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
import type { SessionSnapshot, SessionStatus, SessionTokens, SessionUser } from './types'
import {
  readPersistedRefreshToken,
  persistRefreshToken,
  clearPersistedRefreshToken,
} from '../storage/session-token-store'

// ── In-memory state ────────────────────────────────────────────────────────
// The access token is NOT part of the React snapshot on purpose (see types.ts).
let accessToken: string | null = null
let snapshot: SessionSnapshot = { status: 'unknown', user: null }

// An array, not a Set, on purpose: `Set.prototype.delete` reads as a data
// mutation to the require-authz-check lint (it cannot know this is a listener
// registry, not a repository), and Array push/filter carry no such collision.
let listeners: Array<() => void> = []

function emit(): void {
  // Iterate a snapshot so a listener that unsubscribes mid-emit is safe.
  for (const listener of [...listeners]) listener()
}

export const sessionStore = {
  /** Subscribe for useSyncExternalStore. Returns an unsubscribe. */
  subscribe(listener: () => void): () => void {
    listeners.push(listener)
    return () => {
      listeners = listeners.filter((l) => l !== listener)
    }
  },

  /** Stable snapshot — same reference until a real change (required by React). */
  getSnapshot(): SessionSnapshot {
    return snapshot
  },

  /** The in-memory access token (never persisted). */
  getAccessToken(): string | null {
    return accessToken
  },

  /** The persisted refresh token (storage layer), or null. */
  getRefreshToken(): string | null {
    return readPersistedRefreshToken()
  },

  /** Establish a fully authenticated session (after a successful login). */
  setSession(user: SessionUser, tokens: SessionTokens): void {
    accessToken = tokens.accessToken
    persistRefreshToken(tokens.refreshToken)
    snapshot = { status: 'authenticated', user }
    emit()
  },

  /**
   * Replace the token pair without touching user/status — used by a silent
   * refresh. Does not emit unless it needs to: tokens are not in the snapshot.
   */
  setTokens(tokens: SessionTokens): void {
    accessToken = tokens.accessToken
    persistRefreshToken(tokens.refreshToken)
  },

  /** Set the lifecycle status (user preserved). No-op if unchanged. */
  setStatus(status: SessionStatus): void {
    if (snapshot.status === status) return
    snapshot = { status, user: snapshot.user }
    emit()
  },

  /**
   * End the session, fail-closed: drop the in-memory access token, forget the
   * persisted refresh token, and move to `unauthenticated`. This is the ONLY
   * transition on any auth failure — an error is never permission to stay in.
   */
  clear(): void {
    accessToken = null
    clearPersistedRefreshToken()
    if (snapshot.status !== 'unauthenticated' || snapshot.user !== null) {
      snapshot = { status: 'unauthenticated', user: null }
    }
    emit()
  },
}
